import { mix, Pix } from '../icons/pix';
import { CORNER, FRAME_H, frameSource, nineSlice, TILE } from './frame';
import { GOLD, M, RED } from './palette';
import { Pen } from './pen';
import type { Box, PlateAnim, PlateDef, PlateSprite, Tassel, Tones } from './registry';

/**
 * Puts a plate together, without a canvas (so it is tested in Node): the ornaments behind, the
 * 9-slice frame stretched to the title, the tassels' cords and charms, the knobs and ornaments
 * in front, each layer outlined in the plate's ink. The lettering and anything that moves (the
 * tassels' threads, petals, flames, clouds) are placed over it each frame (plates/index.ts).
 */

/** Where everything on a plate is, in units on its canvas. */
export interface PlateGeom {
  /** The canvas, in units. */
  readonly w: number;
  readonly h: number;
  readonly frame: Box;
  /** The lettering's centre. */
  readonly tx: number;
  readonly ty: number;
  /** The tassels' threads: where each hangs (its top centre) and its look. */
  readonly threads: readonly {
    readonly x: number;
    readonly y: number;
    readonly len: number;
    readonly key: string;
  }[];
}

/** The tassels a plate hangs when it names none: two red ones under the corner fittings. */
const DEFAULT_TASSELS: readonly Tassel[] = [{ x: 3 }, { x: -4 }];

const tasselsOf = (def: PlateDef) => def.tassels ?? DEFAULT_TASSELS;

/** Room drawn round the frame for ornaments before the picture is cropped to them, in units. */
const ROOM = 48;

/**
 * The layout of a plate for lettering `tw` units wide, its frame's top left at (x, y) on a canvas
 * `w`×`h` units.
 */
function layout(def: PlateDef, tw: number, x: number, y: number, w: number, h: number): PlateGeom {
  const [il, ir] = def.inset;
  const fw = Math.max(CORNER * 2 + TILE, Math.ceil(tw) + il + ir);
  const frame: Box = {
    x,
    y,
    w: fw,
    h: FRAME_H,
    r: x + fw,
    b: y + FRAME_H,
    cx: x + fw / 2,
    cy: y + FRAME_H / 2,
  };
  const threads = tasselsOf(def).map((ts) => {
    const tx = ts.x >= 0 ? frame.x + ts.x : frame.r + ts.x;
    return { x: tx, y: frame.b + (ts.cord ?? 2) + charmH(ts), len: ts.len ?? 6, key: threadKey(ts) };
  });
  return { w, h, frame, tx: frame.x + il + (fw - il - ir) / 2, ty: frame.cy, threads };
}

const charmH = (ts: Tassel) =>
  ts.charm === 'coin' ? 5 : ts.charm === 'jade' ? 4 : ts.charm === 'bell' ? 4 : ts.charm === 'bead' ? 2 : 3;

const threadKey = (ts: Tassel) => `${(ts.tones ?? RED).join('')}|${ts.len ?? 6}`;

/** A charm on a tassel's cord, its top centre at (x, y). */
function charm(p: Pen, ts: Tassel, x: number, y: number): void {
  const [h, m, l] = ts.charmTones ?? (ts.charm === 'knot' || !ts.charm ? (ts.tones ?? RED) : GOLD);
  switch (ts.charm ?? 'knot') {
    case 'coin':
      p.rect(x - 2, y, 5, 5, m)
        .rect(x - 1, y, 3, 1, h)
        .rect(x + 2, y + 1, 1, 3, h);
      p.rect(x - 2, y + 4, 4, 1, l).px(x, y + 2, mix(l, M.ink, 0.5));
      p.clear(x - 2, y, 1, 1)
        .clear(x + 2, y, 1, 1)
        .clear(x - 2, y + 4, 1, 1)
        .clear(x + 2, y + 4, 1, 1);
      break;
    case 'jade':
      p.ell(x + 0.5, y + 2, 2, 2, m)
        .px(x + 1, y + 1, h)
        .px(x - 1, y + 3, l);
      break;
    case 'bead':
      p.rect(x - 1, y, 2, 2, m).px(x, y, h);
      p.rect(x, y, 1, 2, m);
      break;
    case 'bell':
      p.rect(x - 1, y, 3, 1, h)
        .rect(x - 1, y + 1, 3, 2, m)
        .rect(x - 2, y + 3, 5, 1, l)
        .px(x + 1, y + 1, h);
      break;
    default:
      // A Chinese knot: a small diamond with a light top-right face.
      p.rect(x - 1, y, 3, 3, m)
        .px(x, y, h)
        .px(x + 1, y + 1, h)
        .px(x - 1, y + 2, l);
      p.clear(x - 1, y, 1, 1).clear(x + 1, y + 2, 1, 1);
  }
}

/** A tassel's threads: a gold cap, then threads, shaded left and tapering; `lean` -1, 0 or 1. */
export function threadSprite(tones: Tones, len: number, lean: number): PlateSprite {
  return {
    w: 5,
    h: len + 2,
    draw(p) {
      const [h, m, l] = tones;
      p.rect(1, 0, 3, 1, M.gold).px(3, 0, M.goldHi);
      for (let j = 1; j <= len; j++) {
        const off = 1 + (j > len / 2 ? lean : 0);
        const narrow = j === len;
        p.rect(off + (narrow ? 0.5 : 0), j, narrow ? 2 : 3, 1, m);
        if (!narrow) p.px(off + 2, j, h).px(off, j, l);
      }
    },
  };
}

/** Where a thread's sprite is for each way it leans. */
export const threadName = (key: string, lean: number): string => `~${key}|${lean}`;

/** The thread sprites of a plate's tassels, by name. */
export function threadSprites(def: PlateDef): Record<string, PlateSprite> {
  const out: Record<string, PlateSprite> = {};
  for (const ts of tasselsOf(def))
    for (const lean of [-1, 0, 1])
      out[threadName(threadKey(ts), lean)] = threadSprite(ts.tones ?? RED, ts.len ?? 6, lean);
  return out;
}

/** The gold knob on each end of the frame, at mid height. */
function knobs(p: Pen, f: Box): void {
  const rows = ['...h', '..hm', '.hmm', 'hmml', '.lml', '..ll', '...l'];
  const cols = { h: M.goldHi, m: M.gold, l: M.goldLo };
  p.sprite(f.x - 4, f.cy - 3.5, rows, cols);
  p.sprite(f.r, f.cy - 3.5, rows, cols, true);
}

/** Outlines a layer in ink, a pixel round every drawn pixel. */
const inked = (layer: Pen, ink: string) => layer.pix.outline(ink);

/**
 * The still picture of a plate for lettering `tw` units wide, at `s` pixels per unit: drawn with
 * room all round, then cropped to what was drawn (and the tassels' threads), a unit spare on
 * every side, so ornaments may reach as far as they like.
 */
export function platePix(def: PlateDef, tw: number, s: number): { pix: Pix; geom: PlateGeom } {
  const fw = layout(def, tw, 0, 0, 0, 0).frame.w;
  const W = fw + ROOM * 2;
  const H = FRAME_H + ROOM * 2;
  const big = layout(def, tw, ROOM, ROOM, W, H);
  const f = big.frame;
  const ink = def.ink ?? M.ink;
  const out = new Pen(new Pix(W * s, H * s), s);
  if (def.back) {
    const back = out.layer();
    def.back(back, f);
    inked(back, ink);
    out.over(back);
  }
  const src = frameSource(def, s);
  const k = CORNER * s;
  out.pix.blit(nineSlice(src, k, k, 0, 0, f.w * s, FRAME_H * s), f.x * s, f.y * s);
  const hang = out.layer();
  tasselsOf(def).forEach((ts, i) => {
    const t = big.threads[i]!;
    const cord = ts.cord ?? 2;
    hang.rect(t.x, f.b, 1, cord, mix(M.redLo, ink, 0.2));
    charm(hang, ts, t.x, f.b + cord);
  });
  inked(hang, ink);
  out.over(hang);
  const front = out.layer();
  if (def.knobs !== false) knobs(front, f);
  def.front?.(front, f);
  inked(front, ink);
  out.over(front);
  // Crop to what was drawn, in whole units.
  const pix = out.pix;
  let [x0, y0, x1, y1] = [pix.w, pix.h, -1, -1];
  for (let y = 0; y < pix.h; y++)
    for (let x = 0; x < pix.w; x++)
      if (pix.d[y * pix.w + x]! >>> 24) {
        x0 = Math.min(x0, x);
        x1 = Math.max(x1, x);
        y0 = Math.min(y0, y);
        y1 = Math.max(y1, y);
      }
  if (x0 === 0 || y0 === 0 || x1 === pix.w - 1 || y1 === pix.h - 1)
    throw new Error(`Plate "${def.id}" reaches more than ${ROOM} units past its frame`);
  let u0 = Math.floor(x0 / s) - 1;
  const v0 = Math.floor(y0 / s) - 1;
  let u1 = Math.ceil((x1 + 1) / s) + 1;
  let v1 = Math.ceil((y1 + 1) / s) + 1;
  for (const t of big.threads) {
    u0 = Math.min(u0, t.x - 3);
    u1 = Math.max(u1, t.x + 4);
    v1 = Math.max(v1, t.y + t.len + 3);
  }
  const cut = new Pix((u1 - u0) * s, (v1 - v0) * s);
  for (let y = 0; y < cut.h; y++) {
    const from = (y + v0 * s) * pix.w + u0 * s;
    cut.d.set(pix.d.subarray(from, from + cut.w), y * cut.w);
  }
  return { pix: cut, geom: layout(def, tw, ROOM - u0, ROOM - v0, u1 - u0, v1 - v0) };
}

/** A moving piece of a plate, drawn on its own (outlined in the plate's ink). */
export function spritePix(def: PlateDef, spr: PlateSprite, s: number): Pix {
  // Drawn one unit in, so the outline has room (and placed one unit up and left to match).
  const inner = new Pen(new Pix((spr.w + 2) * s, (spr.h + 2) * s), s);
  const shifted = new Pen(new Pix(spr.w * s, spr.h * s), s);
  spr.draw(shifted);
  inner.pix.blit(shifted.pix, s, s);
  inner.pix.outline(def.ink ?? M.ink);
  return inner.pix;
}

/** Where every moving piece is at time `t`: the plate's own motion, then its tassels' sway. */
export function playPlate(def: PlateDef, geom: PlateGeom, t: number, a: PlateAnim): void {
  def.motion?.play(a, t, geom.frame);
  geom.threads.forEach((th, i) => {
    const lean = Math.max(-1, Math.min(1, Math.round(Math.sin(t * 2.1 + i * 1.7) * 1.3)));
    a.spr(threadName(th.key, lean), th.x - 2, th.y);
  });
}
